<script setup lang="ts">
  import { computed, onMounted } from "vue";
  import { storeToRefs } from "pinia";
  import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
  import { useResultsV2CardsStore } from "@stores/survey/resultsV2/resultsV2.cards";
  import { Select } from "@/components/episto-ui/select";
  import { Switch } from "@/components/ui/switch";
  import OptionRow from "./CardOptionFields/OptionRow.vue";
  import OptionSection from "./CardOptionFields/OptionSection.vue";

  const props = defineProps({
    card: { type: Object, required: true },
  });

  const store = useResultsV2Store();
  const cardsStore = useResultsV2CardsStore();
  const { availableCards, savedPopulations } = storeToRefs(store);

  onMounted(() => {
    if (!savedPopulations.value.length) store.fetchSavedPopulations();
  });

  const LEVEL_OPTIONS = [
    { value: 0.9, label: "90%" },
    { value: 0.95, label: "95%" },
    { value: 0.99, label: "99%" },
  ];

  function questionIdsOf(card) {
    return (card.questions || []).map((q) => q.id).filter((id) => id != null);
  }

  function setContent(patch) {
    cardsStore.updateCardLocal(props.card.id, {
      content: { ...props.card.content, ...patch },
    });
  }

  const cardOptions = computed(() => availableCards.value.map((c) => ({ value: c.id, label: c.name || c.token })));

  const populationOptions = computed(() => savedPopulations.value.map((p) => ({ value: p.token, label: p.name })));

  // --- Rows: selecting a card adds all of its question ids ---

  const rows = computed(() => props.card.content?.rows || []);

  const selectedRowCardIds = computed({
    get: () =>
      availableCards.value.filter((c) => questionIdsOf(c).some((id) => rows.value.includes(id))).map((c) => c.id),
    set: (cardIds) => {
      const ids = (cardIds || []).flatMap((cardId) => {
        const card = availableCards.value.find((c) => c.id === cardId);
        return card ? questionIdsOf(card) : [];
      });
      setContent({ rows: ids });
    },
  });

  // --- Columns: two multi-selects (questions + populations) combined into one heterogeneous list ---

  const columns = computed(() => props.card.content?.columns || []);

  const questionColumnSpecs = computed(() => columns.value.filter((c) => c.type === "question"));
  const populationColumnSpecs = computed(() => columns.value.filter((c) => c.type === "population"));

  const columnQuestionCardIds = computed({
    get: () =>
      availableCards.value
        .filter((c) => questionColumnSpecs.value.some((spec) => questionIdsOf(c).includes(spec.question_id)))
        .map((c) => c.id),
    set: (cardIds) => {
      // A column question is a single question, so we take the card's first question id
      // (multi-question cards like matrices only contribute their leading question here).
      const specs = (cardIds || [])
        .map((cardId) => questionIdsOf(availableCards.value.find((c) => c.id === cardId))[0])
        .filter((id) => id != null)
        .map((question_id) => ({ type: "question", question_id }));
      setContent({ columns: [...specs, ...populationColumnSpecs.value] });
    },
  });

  const columnPopulationTokens = computed({
    get: () => populationColumnSpecs.value.map((spec) => spec.search_group_token),
    set: (tokens) => {
      const specs = (tokens || []).map((search_group_token) => ({ type: "population", search_group_token }));
      setContent({ columns: [...questionColumnSpecs.value, ...specs] });
    },
  });

  // --- Display & significance ---

  const showCounts = computed({
    get: () => props.card.content?.show_counts !== false,
    set: (val) => setContent({ show_counts: val }),
  });

  const pairwise = computed({
    // Off by default — the user opts into significance letters (matches the creation default).
    get: () => props.card.content?.significance?.pairwise ?? false,
    set: (val) => setContent({ significance: { ...props.card.content?.significance, pairwise: val } }),
  });

  const level = computed({
    get: () => props.card.content?.significance?.level ?? 0.95,
    set: (val) => setContent({ significance: { ...props.card.content?.significance, level: val } }),
  });

  const displayCount = computed(() => [!showCounts.value, pairwise.value].filter(Boolean).length);
</script>

<template>
  <OptionSection
    id="data"
    title="Data">
    <OptionRow
      label="Rows"
      hint="Questions displayed as rows, one block per question.">
      <Select
        :model-value="selectedRowCardIds"
        :options="cardOptions"
        placeholder="Select questions..."
        multiple
        filterable
        @update:model-value="selectedRowCardIds = $event" />
    </OptionRow>

    <OptionRow
      label="Column questions"
      hint="Questions whose answers become column groups.">
      <Select
        :model-value="columnQuestionCardIds"
        :options="cardOptions"
        placeholder="Select questions..."
        multiple
        filterable
        @update:model-value="columnQuestionCardIds = $event" />
    </OptionRow>

    <OptionRow
      label="Column populations"
      hint="Saved populations displayed as extra columns.">
      <Select
        :model-value="columnPopulationTokens"
        :options="populationOptions"
        placeholder="Select populations..."
        multiple
        filterable
        @update:model-value="columnPopulationTokens = $event" />
    </OptionRow>
  </OptionSection>

  <OptionSection
    id="display"
    title="Display"
    :count="displayCount">
    <OptionRow
      label="Show counts (N)"
      hint="Show the number of respondents next to each percentage."
      inline>
      <Switch
        :model-value="showCounts"
        @update:model-value="showCounts = $event" />
    </OptionRow>

    <OptionRow
      label="Significance letters"
      hint="Show which columns differ significantly, with a letter per column."
      inline>
      <Switch
        :model-value="pairwise"
        @update:model-value="pairwise = $event" />
    </OptionRow>

    <OptionRow
      v-if="pairwise"
      label="Confidence level"
      hint="Confidence level of the significance test."
      nested>
      <Select
        :model-value="level"
        :options="LEVEL_OPTIONS"
        @update:model-value="level = $event" />
    </OptionRow>
  </OptionSection>
</template>
